import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { Router, ActivatedRoute, ParamMap } from '@angular/router';
import { FormGroup, FormBuilder, FormArray, Form } from '@angular/forms';
import { ServiceMethods } from '@feathersjs/feathers';
import { VznFeathersService } from '@noammazuz/vzn-feathers';
import { Site } from 'src/app/models/site';
import { ChangeDetectionStrategy } from '@angular/compiler/src/compiler_facade_interface';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from 'src/app/components/confirm-dialog/confirm-dialog.component';
import { TranslateService, TranslationChangeEvent } from '@ngx-translate/core';


@Component({
  selector: 'app-site-details-page',
  templateUrl: './site-details-page.component.html',
  styleUrls: ['./site-details-page.component.scss']
})
export class SiteDetailsPageComponent implements OnInit {
  id: string;
  siteForm: FormGroup;
  uploadUrlService: ServiceMethods<any>;
  siteService: ServiceMethods<Site>;
  loading: boolean;
  backgroundColor: string;
  fontColor: string;
  themeColor: string;

  selectionFieldTypes = [
    {
      value: 'radio',
      title: 'רדיו'
    }, {
      value: 'select',
      title: 'בחירה'
    }]

  constructor(
    public translate: TranslateService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private formBuilder: FormBuilder,
    private feathersService: VznFeathersService<any>,
    private changeDetector: ChangeDetectorRef,
    public dialog: MatDialog
  ) {
    this.translate = translate;
    this.uploadUrlService = this.feathersService.service('upload-url');
    this.siteService = this.feathersService.service('sites');

    this.siteForm = this.formBuilder.group({
      name: '',
      formPosition: 'center',
      logoLink: '',
      logoPosition: 'left',
      headerMenuItems: this.formBuilder.array([]),
      spaceLogoAndItems: false,
      title: '',
      titleHtml: '',
      dateTitle: '',
      selectionFieldTitle: '',
      selectionFieldPlaceholder: '',
      selectionFieldErrorMessage: '',
      selectionFieldType: 'radio',
      selectionField: false,
      additionalFields: this.formBuilder.array([]),
      selectionFieldOptions: this.formBuilder.array([]),
      dates: this.formBuilder.array([null, null, null]),
      logo: {},
      bgImage: {},
      contentImage: {},
      realDomain: '',
      phishingDomain: '',
      rightsMessage: '',
      confirmationMessage: '',
      htmlCode: '',
      siteTitle: '',
      formTitle: '',
      formTitleHtml: '',
      submitButtonLabel: '',
      direction: 'rtl',
      userNameTitle: '',
      passwordTitle: '',
      userNamePlaceholder: '',
      passwordPlaceholder: '',
      active: false,
      savePasswords: false,
      footerLogoPosition: 'left',
      favicon: {}
    });
  }

  ngOnInit(): void {
    this.loading = true;
    this.activatedRoute.paramMap.subscribe(async (paramMap: ParamMap) => {
      this.id = paramMap.get('id');

      if (this.id === 'new') {
        this.loading = false;
      } else {
        const site: Site = new Site(await this.siteService.get(this.id));
        this.siteForm.patchValue(site);

        this.siteForm.controls.additionalFields = this.formBuilder.array((site.additionalFields || []).map(field => this.formBuilder.group(field)) || []);
        this.siteForm.controls.additionalFields.setParent(this.siteForm);

        this.siteForm.controls.selectionFieldOptions = this.formBuilder.array(site.selectionFieldOptions || []);
        this.siteForm.controls.selectionFieldOptions.setParent(this.siteForm);

        this.siteForm.controls.headerMenuItems = this.formBuilder.array((site.headerMenuItems || []).map(field => this.formBuilder.group(field)) || []);
        this.siteForm.controls.headerMenuItems.setParent(this.siteForm);

        this.backgroundColor = site.backgroundColor || '#ffffff';
        this.fontColor = site.fontColor || '#000000';
        this.themeColor = site.themeColor || '#e91e63';
        this.loading = false;
      }
      this.changeDetector.detectChanges();
    });
  }

  get additionalFields(): FormArray {
    return this.siteForm.controls.additionalFields as FormArray;
  }

  get headerMenuItems(): FormArray {
    return this.siteForm.controls.headerMenuItems as FormArray;
  }

  get fieldOptions(): FormArray {
    return this.siteForm.controls.selectionFieldOptions as FormArray;
  } 

  get optionCount() {
    return (this.siteForm.controls.selectionFieldOptions as FormArray).controls.length;
  }

  get datesFormArray() {
    return (this.siteForm.get('dates') as FormArray).controls;
  }

  get title() {
    let title: string;
    if (this.id === 'new') {
      title = this.translate.instant('SITE-DETAILS-PAGE.NEW-SITE') // 'אתר חדש';
    } else {
      title = this.translate.instant('SITE-DETAILS-PAGE.EDIT-SITE');
    }

    return title;
  }

  async onSubmit() {
    console.log(this.siteForm.value);
    const data = {
      ...this.siteForm.value,
      backgroundColor: this.backgroundColor,
      fontColor: this.fontColor,
      themeColor: this.themeColor
    };
    if (this.id === 'new') {
      await this.siteService.create(data);
    } else {
      await this.siteService.patch(this.id, data);
    }
    this.router.navigate(['/sites']);
  }

  deleteSite(event: MouseEvent): void {
    event.preventDefault();
    event.stopImmediatePropagation();

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '250px',
      data: { title: this.translate.instant('SITE-DETAILS-PAGE.ALERT-TITLE'), body: this.translate.instant('SITE-DETAILS-PAGE.ALERT-DESC') }
    });

    dialogRef.afterClosed().subscribe(async result => {
      if (result === true) {
        await this.siteService.remove(this.id);
        this.router.navigate(['/sites']);
      }
    });
  }

  addOption() {
    (this.siteForm.controls.selectionFieldOptions as FormArray).push(this.formBuilder.control(''));
    this.changeDetector.markForCheck();
  }

  removeOption(index: number) {
    (this.siteForm.controls.selectionFieldOptions as FormArray).removeAt(index);
    this.changeDetector.markForCheck();
  }

  addAdditionalField() {
    (this.siteForm.controls.additionalFields as FormArray).push(this.formBuilder.group({ title: '', placeholder: '' }));
    this.changeDetector.markForCheck();
  }

  removeAdditionalField(index: number) {
    (this.siteForm.controls.additionalFields as FormArray).removeAt(index);
    this.changeDetector.markForCheck();
  }

  addMenuItem() {
    (this.siteForm.controls.headerMenuItems as FormArray).push(this.formBuilder.group({ title: '', link: '' }));
    this.changeDetector.markForCheck();
  }

  removeMenuItem(index: number) {
    (this.siteForm.controls.headerMenuItems as FormArray).removeAt(index);
    this.changeDetector.markForCheck();
  }

}
